import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  User, Briefcase, Shield, ChevronDown, Check, RefreshCw
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const ROLE_ICONS = {
  customer: User,
  contractor: Briefcase,
  admin: Shield,
  super_admin: Shield,
  support_agent: Shield,
  verification_reviewer: Shield,
  ops_dispatch: Shield,
  finance: Shield
};

const ROLE_LABELS = {
  customer: 'Customer Mode',
  contractor: 'Contractor Mode',
  admin: 'Admin Mode',
  super_admin: 'Super Admin',
  support_agent: 'Support Agent',
  verification_reviewer: 'Verification',
  ops_dispatch: 'Operations',
  finance: 'Finance'
};

const ROLE_COLORS = {
  customer: 'bg-blue-100 text-blue-700',
  contractor: 'bg-green-100 text-green-700',
  admin: 'bg-purple-100 text-purple-700',
  super_admin: 'bg-red-100 text-red-700'
};

const RoleSwitcher = () => {
  const { 
    activeRole, 
    availableRoles, 
    canSwitchRoles, 
    switchRole,
    hasAdminAccess
  } = useAuth();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);

  if (!canSwitchRoles && !hasAdminAccess) {
    return null;
  }

  const handleRoleSwitch = async (newRole) => {
    if (newRole === activeRole) return;
    
    setSwitching(true);
    const result = await switchRole(newRole);
    setSwitching(false);
    
    if (result.success) {
      toast.success(`Switched to ${ROLE_LABELS[newRole] || newRole}`);
      
      // Navigate to appropriate dashboard
      if (newRole === 'customer') {
        navigate('/dashboard');
      } else if (newRole === 'contractor') {
        navigate('/contractor/dashboard');
      } else if (['admin', 'super_admin'].includes(newRole)) {
        navigate('/admin');
      }
    } else {
      toast.error(result.error || 'Failed to switch role');
    }
  };

  const CurrentIcon = ROLE_ICONS[activeRole] || User;
  const currentLabel = ROLE_LABELS[activeRole] || activeRole;
  const currentColor = ROLE_COLORS[activeRole] || 'bg-neutral-100 text-neutral-700';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition ${currentColor} hover:opacity-80`}
          data-testid="role-switcher"
          disabled={switching}
        >
          {switching ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <CurrentIcon className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">{currentLabel}</span>
          <ChevronDown className="w-3 h-3" />
        </button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Switch Mode</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {availableRoles.map((role) => {
          const RoleIcon = ROLE_ICONS[role] || User;
          const isActive = role === activeRole;
          
          return (
            <DropdownMenuItem
              key={role}
              onClick={() => handleRoleSwitch(role)}
              className={`cursor-pointer ${isActive ? 'bg-neutral-100' : ''}`}
              data-testid={`switch-to-${role}`}
            >
              <RoleIcon className="w-4 h-4 mr-2" />
              <span className="flex-1">{ROLE_LABELS[role] || role}</span>
              {isActive && <Check className="w-4 h-4 text-green-600" />}
            </DropdownMenuItem>
          );
        })}
        
        {hasAdminAccess && !availableRoles.includes('admin') && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => navigate('/admin')}
              className="cursor-pointer"
              data-testid="go-to-admin"
            >
              <Shield className="w-4 h-4 mr-2" />
              <span>Admin Panel</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RoleSwitcher;
